import React from 'react';
import PropTypes from 'prop-types';
import { Badge } from 'Components/Badge';
import Icon from 'Components/Icon';
import { classnames } from 'Utils/classnames';
import { useMixpanel } from 'Utils/mixpanel';
import { THEMES, THEME_PREFERENCES, THEME_CLICK_POINTS, trackClickThemeButton } from 'Utils/theme';
import useUserTheme from '../useUserTheme';

const ThemeSegmentedControl = ({ className, compact, clickPoint }) => {
  const [theme, changeTheme, cms, preference] = useUserTheme();
  const mixpanel = useMixpanel();
  const isVisible = compact ? cms.isDarkModeEnabled : cms.isHamburgerThemeEnabled;
  const { labels } = cms;
  const options = [
    { value: THEME_PREFERENCES.LIGHT, iconName: 'svgSun', label: labels.light },
    { value: THEME_PREFERENCES.DARK, iconName: 'svgMoon', label: labels.dark },
    { value: THEME_PREFERENCES.SYSTEM, iconName: 'svgSystemPhone', label: labels.system }
  ];

  const handleThemeClick = (event, nextTheme) => {
    if (compact) {
      event.stopPropagation();
    }
    changeTheme(nextTheme);
    trackClickThemeButton(mixpanel, nextTheme, clickPoint);
  };

  const handleToggleClick = event => {
    handleThemeClick(event, theme === THEMES.LIGHT ? THEMES.DARK : THEMES.LIGHT);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={ classnames({
      'theme-segmented-control': true,
      'theme-segmented-control--compact': compact,
      [className]: !!className
    }) }>
      { !compact && (
        <div className="theme-segmented-control__header">
          <span className="theme-segmented-control__title">{ labels.title }</span>
          <Badge isNew className="theme-segmented-control__badge">Yeni</Badge>
        </div>
      ) }
      { compact ? (
        <button
          type="button"
          role="switch"
          title={ theme === THEMES.LIGHT ? labels.dark : labels.light }
          className="theme-segmented-control__options theme-segmented-control__options--switch"
          onClick={ handleToggleClick }
          aria-checked={ theme === THEMES.DARK }
          aria-label={ labels.title }
        >
          <span className={ classnames({
            'theme-segmented-control__option': true,
            'theme-segmented-control__option--active': theme === THEMES.LIGHT
          }) }>
            <Icon iconName="svgSun" className="theme-segmented-control__option-icon" />
          </span>
          <span className={ classnames({
            'theme-segmented-control__option': true,
            'theme-segmented-control__option--active': theme === THEMES.DARK
          }) }>
            <Icon iconName="svgMoon" className="theme-segmented-control__option-icon" />
          </span>
        </button>
      ) : (
        <div className="theme-segmented-control__options" role="group" aria-label={ labels.title }>
          { options.map(option => (
            <button
              key={ option.value }
              type="button"
              title={ option.label }
              className={ classnames({
                'theme-segmented-control__option': true,
                'theme-segmented-control__option--active': preference === option.value
              }) }
              onClick={ event => handleThemeClick(event, option.value) }
              aria-pressed={ preference === option.value }
            >
              <Icon iconName={ option.iconName } className="theme-segmented-control__option-icon" />
              <span>{ option.label }</span>
            </button>
          )) }
        </div>
      ) }
    </div>
  );
};

ThemeSegmentedControl.propTypes = {
  className: PropTypes.string,
  compact: PropTypes.bool,
  clickPoint: PropTypes.string
};

ThemeSegmentedControl.defaultProps = {
  compact: false,
  clickPoint: THEME_CLICK_POINTS.HAMBURGER
};

export default ThemeSegmentedControl;
